import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';
import { CameraIntegration } from '@sb/integrations/types/cameraTypes';

import { Expression } from '../../types';
import {
  Detection3DPoseEstimationConfig,
  LocateMethod,
  ResultOrdering,
} from '../../vision/LocateTypes';
import { RegionOfInterest } from '../../vision/VisionInterface';

export const PlaneDetectionModeEnum = zod.enum([
  'Manual',
  'Geometric',
  'Learned',
]);

export const PlaneDetectionMode =
  PlaneDetectionModeEnum.optional().default('Manual');

export type PlaneDetectionModeType = zod.infer<typeof PlaneDetectionMode>;

// Match defaults from python/libs/depth/geometric_plane.py
export const DEFAULT_RANSAC_MAX_ITERATIONS = 1000;
export const DEFAULT_RANSAC_DISTANCE_THRESHOLD = 0.005;
export const DEFAULT_RANSAC_MIN_SAMPLES = 3;

export const RANSAC_QUALITY_LEVELS = {
  Fast: 100,
  Balanced: 500,
  Accurate: 1000,
} as const;

export type RansacQuality = keyof typeof RANSAC_QUALITY_LEVELS;

export const RansacParams = zod.object({
  maxIterations: zod.number().optional().default(DEFAULT_RANSAC_MAX_ITERATIONS),
  distanceThreshold: zod
    .number()
    .optional()
    .default(DEFAULT_RANSAC_DISTANCE_THRESHOLD),
  minSamples: zod.number().optional().default(DEFAULT_RANSAC_MIN_SAMPLES),
});

// Tilt angle threshold: reject detections where the surface normal
// deviates from vertical by more than this angle (degrees).
// Min 1° (0 is reserved as a "disabled" sentinel on the backend), 90 = accept all.
export const DEFAULT_MAX_TILT_ANGLE = 15;

export default zod.object({
  camera: CameraIntegration.optional(),
  // Optional equipment ID for camera-bot-cpp mode (when set, camera field is ignored)
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  method: LocateMethod,
  positionListID: zod.string(), // The position list to output results to
  filters: zod.object({
    resultsLimit: zod.optional(zod.number()), // The max number of results to output
  }),
  resultOrdering: ResultOrdering.optional(),
  transform: CartesianPose,
  planeID: zod.string().optional(), // Required for Manual mode, unused for Geometric/Learned
  objectHeight: zod.number(), // in meters
  planeDetectionMode: PlaneDetectionMode,
  ransacParams: RansacParams.optional(),
  maxTiltAngle: zod
    .number()
    .min(1)
    .max(90)
    .optional()
    .default(DEFAULT_MAX_TILT_ANGLE),
  disablePlaneOrientation: zod.boolean().optional().default(false),
  // 3D-detection projection for the live preview path. Optional and ignored by
  // the routine Locate step runtime (Step/Locate/Step.ts); consumed by the
  // wizard's live preview via getLocateArgsFromLocateStepArgs.
  projectionMode: zod.enum(['Manual', 'Detection3D']).optional(),
  detection3dConfig: Detection3DPoseEstimationConfig.optional(),
  captureConfig: zod
    .object({
      saveEquipmentId: zod.string(),
      saveFilepath: Expression,
      saveMetadata: zod.boolean().default(false),
    })
    .optional(),
});
